/**
 * Native Angular components
 */
import {BrowserModule} from '@angular/platform-browser';
import {APP_INITIALIZER, NgModule} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {HttpClient, HttpClientModule} from '@angular/common/http';
/**
 * External Native components
 */
import {TranslateLoader, TranslateModule, TranslateService} from '@ngx-translate/core';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
/**
 * Application routing developed by Numen
 */
import {AppRoutingModule} from './routing/app-routing.module';
import {AuthGuard} from './routing/authGuard';
import {SafeUrlPipe} from './pipes/app-custom-pipes';
/**
 * Application Components developed by Numen
 */
import {AppComponent} from './app.component';
import {LoginComponent} from './application/login/login.component';
import {LogoutComponent} from './application/logout/logout.component';
import {LoaderComponent} from './application/loader/loader.component';
import {ApplicationContainerComponent} from './application/application-container/application-container.component';
import {NavbarComponent} from './application/application-container/navbar/navbar.component';
import {LeftContainerComponent} from './application/application-container/left-container/left-container.component';
import {RightContainerComponent} from './application/application-container/right-container/right-container.component';
import {ConfigurationComponent} from './application/application-container/configuration/configuration.component';
import {ForgottenPasswordComponent} from './application/login/forgotten-password/forgotten-password.component';
import {UploadComponent} from './application/application-container/upload/upload.component';
import {ApplicationComponent} from './application/application.component';
import {NewPasswordComponent} from './application/new-password/new-password.component';
import {CreatePasswordComponent} from './application/commons/create-password/create-password.component';
import {FilterComponent} from './application/application-container/left-container/search/filter/filter.component';
import {FoldsListComponent} from './application/application-container/right-container/search-result/folds-list/folds-list.component';
import {SearchComponent} from './application/application-container/left-container/search/search.component';
import {SearchResultComponent} from './application/application-container/right-container/search-result/search-result.component';
import {FoldDetailComponent} from './application/application-container/commons/fold-detail-and-action/fold-detail/fold-detail.component';
import {FoldActionComponent} from './application/application-container/commons/fold-detail-and-action/fold-action/fold-action.component';
import {CustomListComponent} from './application/application-container/commons/custom-list/custom-list.component';
import {ActionConfirmComponent} from './application/application-container/commons/fold-detail-and-action/fold-action/action-confirm/action-confirm.component';
import {AddressComponent} from './application/application-container/commons/fold-detail-and-action/fold-action/address/address.component';
import {FilesUploadComponent} from './application/application-container/upload/files-upload/files-upload.component';
// noinspection TsLint
import {
  GetOrRemoveOrScheduleSavedRequestComponent
} from './application/application-container/left-container/search/savedRequest/get-or-remove-or-schedule-saved-request/get-or-remove-or-schedule-saved-request';
import {AddOrUpdateSavedRequestComponent} from './application/application-container/left-container/search/savedRequest/add-or-update-saved-request/add-or-update-saved-request';
import {SchedulingComponent} from './application/application-container/left-container/search/savedRequest/get-or-remove-or-schedule-saved-request/scheduling/scheduling.component';
import {SignatureAndValidationComponent} from './application/application-container/left-container/signature-and-validation/signature-and-validation.component';
import {ActionAndSpaceChoiceComponent} from './application/application-container/left-container/signature-and-validation/action-and-space-choice/action-and-space-choice.component';
import {GroundComponent} from './application/application-container/left-container/signature-and-validation/ground/ground.component';
/**
 * Application Services developed by Numen
 */
import {StorageService} from './services/storage/storage.service';
import {SearchService} from './services/apiServices/business/search.service';
import {ConsultCriteriaService} from './services/apiServices/business/consult-criteria.service';
import {AuthenticationService} from './services/apiServices/security/authentication.service';
import {HttpService} from './services/apiServices/http-service.service';
import {PasswordService} from './services/apiServices/security/password.service';
import {FoldDetailService} from './services/apiServices/business/fold-detail.service';
import {UpdateSearchFiltersService} from './services/crossComponent/update-search-filters.service';
import {AddressService} from './services/apiServices/business/address.service';
import {CustomTableSaverService} from './services/storage/custom-table-saver.service';
import {TreatmentChainService} from './services/apiServices/business/treatment-chain.service';
import {CreateDepositService} from './services/apiServices/business/create-deposit.service';
import {UserSavedRequestService} from './services/apiServices/business/user-saved-request.service';
import {KindOfAcceptedFilesService} from './services/apiServices/business/kind-of-accepted-files.service';
import {WorkflowService} from './services/apiServices/business/workflow-service';
import {VirtualPrinterService} from './services/apiServices/business/virtual-printer.service';
import {FoldWorkflowListComponent} from './application/application-container/left-container/signature-and-validation/fold-workflow-list/fold-workflow-list';
/**
 * Application Componenents from various libraries
 */
/*
import {PdfViewerModule} from 'ng2-pdf-viewer';
*/
/**
 * Application services from PrimeNg
 */
import {ConfirmationService, MessageService, SharedModule} from 'primeng/api';
/**
 * Application Component from PrimeNG
 */
import {TableModule} from 'primeng/table';
import {MessagesModule} from 'primeng/messages';
import {KeyFilterModule} from 'primeng/keyfilter';
import {JwtModule} from '@auth0/angular-jwt';
import {UserManagementComponent} from './application/application-container/left-container/user-management/user-management.component';
import {UserManagementService} from './services/apiServices/business/user-management.service';
import {GroupManagementComponent} from './application/application-container/left-container/group-management/group-management.component';
import {DownloadDepositComponent} from './application/download-deposit/download-deposit.component';
import {SuiviRecoComponent} from './application/application-container/commons/suivi-reco/suivi-reco.component';
import {AddressLabelsComponent} from './application/application-container/commons/fold-detail-and-action/fold-action/address/address-labels/address-labels.component';
import {AddressLabelComponent} from './application/application-container/commons/fold-detail-and-action/fold-action/address/address-labels/address-label/address-label.component';
import {CreateSpoolComponent} from './application/commons/create-spool/create-spool.component';
import {DownloadReportFileComponent} from './application/download-report-file/download-report-file.component';
import {DndDirective} from './directives/dnd/dnd.directive';
import {GroupAddressComponent} from './application/application-container/left-container/group-management/address/group-address.component';
import {VirtualPrinterComponent} from './application/virtual-printer/virtual-printer.component';
import {UploadVirtualPrinterComponent} from './application/virtual-printer/upload/upload.component';
import {DownloadVirtualPrinterComponent} from './application/virtual-printer/download/download.component';
import {DownloadGenericFormComponent} from './application/virtual-printer/download-generic-form/download-generic-form.component';
import {MultiFoldActionComponent} from './application/application-container/commons/fold-detail-and-action/multi-fold-action/multi-fold-action.component';
import {MultiActionConfirmComponent} from './application/application-container/commons/fold-detail-and-action/multi-fold-action/multi-action-confirm/multi-action-confirm.component';
import {GenerateReportComponent} from './application/application-container/left-container/search/generate-report/generate-report-component';
import {ReportGeneratorService} from './services/apiServices/business/report-generator.service';
import {NotifyComponent} from './application/application-container/left-container/signature-and-validation/notify/notify.component';
import {ImportUsersComponent} from './application/application-container/left-container/group-management/import-users/import-users.component';
import {AppParameterService} from './services/apiServices/business/app-parameter.service';
import {AngularDraggableModule} from 'angular2-draggable';
import {ExpressAddressComponent} from './application/application-container/upload-express/address/express-address.component';
import {SideBarComponent} from './application/application-container/side-bar/side-bar.component';
import {ExpressMailingComponent} from './application/application-container/upload-express/express-mailing/express-mailing.component';
import {ExpressErlComponent} from './application/application-container/upload-express/express-erl/express-erl.component';
import {ExpressUnitComponent} from './application/application-container/upload-express/express-unit/express-unit.component';
import {ExpressSpoolComponent} from './application/application-container/upload-express/express-spool/express-spool.component';
import {ExpressDepositComponent} from './application/application-container/upload-express/express-deposit/express-deposit.component';
import {MailingModelEditorComponent} from './application/application-container/upload-express/mailing-model-editor/mailing-model-editor.component';
import {DepositOptionsComponent} from './application/application-container/upload-express/deposit-options/deposit-options.component';
import {SignatureOptionsComponent} from './application/application-container/upload-express/signature-options/signature-options.component';
import {DepositReviewComponent} from './application/application-container/upload-express/deposit-review/deposit-review.component';
import {GroupChainsComponent} from './application/application-container/left-container/group-management/group-chains/group-chains.component';
import {TreeTableModule} from 'primeng/treetable';
import {AutoCompleteModule} from 'primeng/autocomplete';
import {InputTextModule} from 'primeng/inputtext';
import {ButtonModule} from 'primeng/button';
import {CheckboxModule} from 'primeng/checkbox';
import {ChipsModule} from 'primeng/chips';
import {PaginatorModule} from 'primeng/paginator';
import {TabViewModule} from 'primeng/tabview';
import {InputSwitchModule} from 'primeng/inputswitch';
import {SpinnerModule} from 'primeng/spinner';
import {RadioButtonModule} from 'primeng/radiobutton';
import {PasswordModule} from 'primeng/password';
import {DialogModule} from 'primeng/dialog';
import {DropdownModule} from 'primeng/dropdown';
import {CalendarModule} from 'primeng/calendar';
import {MenubarModule} from 'primeng/menubar';
import {DragDropModule} from 'primeng/dragdrop';
import {InputTextareaModule} from 'primeng/inputtextarea';
import {FieldsetModule} from 'primeng/fieldset';
import {SelectButtonModule} from 'primeng/selectbutton';
import {SplitButtonModule} from 'primeng/splitbutton';
import {StepsModule} from 'primeng/steps';
import {FileUploadModule} from 'primeng/fileupload';
import {ProgressSpinnerModule} from 'primeng/progressspinner';
import {PickListModule} from 'primeng/picklist';
import {TooltipModule} from 'primeng/tooltip';
import {ConfirmDialogModule} from 'primeng/confirmdialog';
import {ListboxModule} from 'primeng/listbox';
import {PanelModule} from 'primeng/panel';
import {ToastModule} from 'primeng/toast';
import {SliderModule} from 'primeng/slider';
import {FileSaverModule} from 'ngx-filesaver';
import { ModComponent } from './application/application-container/configuration/mod/mod.component';
import {InputNumberModule} from 'primeng/inputnumber';
import { ExpressConfigComponent } from './application/application-container/configuration/express-config/express-config.component';
import { PurgeConfigComponent } from './application/application-container/configuration/purge-config/purge-config.component';
import { ImpersonationComponent } from './application/application-container/configuration/impersonation/impersonation.component';
import { EmailAddressComponent } from './application/application-container/upload-express/email-address/email-address.component';
import {
  DepositErlInfoComponent
} from "./application/application-container/upload-express/express-erl/deposit-erl-info/deposit-erl-info.component";
import {
  DepositErlRecipientComponent
} from "./application/application-container/upload-express/express-erl/deposit-erl-info/deposit-erl-recipient/deposit-erl-recipient.component";
import {ActionLoggingComponent} from "./application/application-container/action-logging/action-logging.component";
import {ErlConfigComponent} from "./application/application-container/configuration/erl-config/erl-config.component";
import {
  EmailConfigComponent
} from "./application/application-container/configuration/email-config/email-config.component";
import {AccordionModule} from "primeng/accordion";
import {MultiSelectModule} from "primeng/multiselect";


/**
 * default htppClient for translation purpose
 */
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}
export function appInitializerFactory(translate: TranslateService) {
  return () => {
    translate.setDefaultLang('fr');
    return translate.use('fr').toPromise();
  };
}
@NgModule({
  declarations: [
    AppComponent, LoginComponent, LogoutComponent, ApplicationContainerComponent, GroupAddressComponent,
    LoaderComponent, NavbarComponent, LeftContainerComponent,
    SearchComponent, RightContainerComponent, ConfigurationComponent, SideBarComponent,
    ForgottenPasswordComponent, UploadComponent, ApplicationComponent, DownloadGenericFormComponent,
    VirtualPrinterComponent, DownloadVirtualPrinterComponent,
    NewPasswordComponent, CreatePasswordComponent, FilterComponent, NotifyComponent,
    FoldsListComponent, SearchResultComponent, FoldDetailComponent, ImportUsersComponent,
    FoldActionComponent, CustomListComponent, DownloadDepositComponent, DndDirective,
    ActionConfirmComponent, MultiActionConfirmComponent, GenerateReportComponent, DepositReviewComponent,
    ExpressAddressComponent, AddressComponent, FilesUploadComponent, DownloadReportFileComponent,
    AddressLabelsComponent, AddressLabelComponent, CreateSpoolComponent, MultiFoldActionComponent,
    SafeUrlPipe, UserManagementComponent, GroupManagementComponent, SuiviRecoComponent,
    GetOrRemoveOrScheduleSavedRequestComponent, AddOrUpdateSavedRequestComponent, SchedulingComponent,
    SignatureAndValidationComponent, ActionAndSpaceChoiceComponent, FoldWorkflowListComponent, GroundComponent,
    MultiFoldActionComponent, MultiActionConfirmComponent, ImportUsersComponent, ExpressMailingComponent,ExpressErlComponent, DepositErlInfoComponent, ExpressUnitComponent,
    ExpressSpoolComponent, ExpressDepositComponent, MailingModelEditorComponent, DepositOptionsComponent, DepositErlRecipientComponent, SignatureOptionsComponent, GroupChainsComponent,
    UploadVirtualPrinterComponent,ActionLoggingComponent,
    ModComponent,
    EmailConfigComponent,
    ErlConfigComponent,
    ExpressConfigComponent,
    PurgeConfigComponent,
    ImpersonationComponent,
    EmailAddressComponent
  ],
    imports: [
        AppRoutingModule, AutoCompleteModule, InputTextModule, InputNumberModule, ButtonModule, BrowserModule, MessagesModule,
        HttpClientModule, FormsModule, ReactiveFormsModule, CheckboxModule, KeyFilterModule,
        InputSwitchModule, TabViewModule, PaginatorModule, ChipsModule,
        TreeTableModule, TableModule, SharedModule, SpinnerModule, SpinnerModule,
        DropdownModule, DialogModule, PasswordModule, RadioButtonModule,
        CalendarModule, MenubarModule, BrowserAnimationsModule, PanelModule,
        FieldsetModule, InputTextareaModule, DragDropModule, AngularDraggableModule,
        SelectButtonModule, SplitButtonModule, StepsModule, FileUploadModule, ProgressSpinnerModule,
        ListboxModule, ConfirmDialogModule, TooltipModule, JwtModule, PickListModule, ToastModule,
        SliderModule, FileSaverModule, MultiSelectModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient]
            }
        }), AccordionModule
    ],
  providers: [
    AuthenticationService, HttpService, PasswordService, HttpClient,
    AuthGuard, StorageService, SearchService, UserManagementService, AppParameterService,
    FoldDetailService, ConsultCriteriaService, MessageService, MessagesModule,
    UpdateSearchFiltersService, AddressService, CustomTableSaverService,
    ConfirmationService, TreatmentChainService, CreateDepositService, ReportGeneratorService,
    UserSavedRequestService, KindOfAcceptedFilesService, WorkflowService, VirtualPrinterService,
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [TranslateService],
      multi: true
    }
  ],
  bootstrap: [AppComponent]
})

export class AppModule {
}
